<template>
  <CContainer>
    <CRow class="justify-content-center">
      <CCol md="12" class="text-start">
        <CRow>
          <CCol class="d-flex">
            <div class="v3-module-list-title" role="heading" aria-level="2">
              Kiosk
            </div>
            <HelpCenterButton classes="ms-2" content-key="module_kiosk_0002" />
          </CCol>
          <CCol class="d-flex flex-row-reverse align-items-center">
            <CustomSwitch
              type="radio"
              test-id="kiosk-switch"
              aria-label="Kiosk, toggle module on or off"
              :model-value="state.isActive"
              @change="toggleKiosk(state.isActive)"
            ></CustomSwitch>
          </CCol>
        </CRow>
        <div class="v3-module-list-box" v-if="state.isActive">
          <div class="d-flex flex-column">
            <div class="v3-module-list-checkbox d-flex">
              <CustomCheck
                v-model="state.form.studentNumberAsKioskPasscode"
                label="Set Kiosk Passcode to be Student Number"
                @change="openKioskPasscodeActionDialog"
                :key="setPasscodeCheckbox"
                :disabled="isPasscodeCheckboxDisabled"
              ></CustomCheck>
            </div>
            <span class="small ms-4">
              (Recommended, Student Number must be 4+ characters long)</span
            >
          </div>
          <div class="v3-module-list-checkbox d-flex mt-2">
            <CustomCheck
              v-model="state.form.hideStudentKioskPasscodeField"
              label="Block students from setting and changing their own passcode"
              @change="submit"
            ></CustomCheck>
          </div>
          <div class="d-flex flex-column mt-4">
            <div class="v3-module-list-checkbox d-flex">
              <span class="checkbox-label flex-1">Student input method</span>
            </div>
            <div class="d-flex flex-wrap gap-3 ms-4 mt-1">
              <CustomCheck
                :model-value="state.form.studentInputMethod === 'emailPasscode'"
                label="Email/Passcode"
                @change="() => handleStudentInputMethodChange('emailPasscode')"
              ></CustomCheck>
              <CustomCheck
                :model-value="state.form.studentInputMethod === 'barcode'"
                label="Barcode"
                @change="() => handleStudentInputMethodChange('barcode')"
              ></CustomCheck>
              <CustomCheck
                :model-value="state.form.studentInputMethod === 'magneticStrip'"
                label="Magnetic Strip Card"
                @change="() => handleStudentInputMethodChange('magneticStrip')"
              ></CustomCheck>
              <CustomCheck
                :model-value="
                  state.form.studentInputMethod === 'studentNumberOnly'
                "
                label="Student Number Only"
                @change="
                  () => handleStudentInputMethodChange('studentNumberOnly')
                "
              ></CustomCheck>
            </div>
          </div>
          <div class="d-flex flex-column mt-4">
            <div class="v3-module-list-checkbox d-flex">
              <CustomCheck
                v-model="state.form.urlKloginMethodOnly"
                label="Disable the ability to direct launch a kiosk"
                @change="submit"
              ></CustomCheck>
            </div>
          </div>
        </div>
        <p
          class="mt-2 text-start v3-module-list-title"
          role="heading"
          aria-level="3"
        >
          Default Domain for Kiosk:
        </p>
        <CRow class="mt-2 mb-2">
          <CustomSelect
            v-model="state.form.defaultDomain"
            :options="schoolDomains"
            placeholder="Default Domain"
            :close-on-select="true"
            @update:model-value="submit"
          />
        </CRow>
        <CAlert
          v-if="state.reqResponse.message"
          dismissible
          class="mt-4"
          :color="state.reqResponse.type"
          :show="!!state.reqResponse.message"
          role="status"
          aria-live="polite"
          @close="resetResponseMessages"
        >
          {{ state.reqResponse.message }}
        </CAlert>
      </CCol>
    </CRow>
  </CContainer>
</template>

<script>
import HelpCenterButton from "../../HelpCenterButton.vue"
import CustomCheck from "@/v3components/shared/Form/CustomCheck.vue"
import CustomSwitch from "@/v3components/shared/Form/CustomSwitch.vue"
import CustomSelect from "@/v3components/shared/Form/CustomSelect.vue"
import { useStore } from "vuex"
import {
  onMounted,
  computed,
  reactive,
  ref,
  inject,
  watch,
  nextTick
} from "vue"

export default {
  name: "KioskModuleForm",
  components: { HelpCenterButton, CustomCheck, CustomSwitch, CustomSelect },
  setup() {
    const store = useStore()

    const actionDialog = inject("actionDialog")

    const setPasscodeCheckbox = ref(0)

    const state = reactive({
      form: {
        urlKloginMethodOnly: false,
        cardAsDefaultLogin: false,
        barcodeAsDefaultLogin: false,
        hideStudentKioskPasscodeField: false,
        studentNumberAsKioskPasscode: false,
        defaultDomain: null,
        studentInputMethod: "emailPasscode",
        studentNumOnly: false
      },
      reqResponse: {
        type: "",
        message: "",
        errors: {}
      },
      isActive: false
    })

    const isLoading = ref(false)

    const schoolDomains = computed(() => store.getters["schools/schoolDomains"])

    const currentUser = computed(() => store.getters["authentication/user"])

    const isPasscodeCheckboxDisabled = computed(() => {
      return (
        state.form.studentInputMethod === "barcode" ||
        state.form.studentInputMethod === "magneticStrip" ||
        state.form.studentInputMethod === "studentNumberOnly"
      )
    })

    const options = computed(() => {
      const index = store.state.schools.activeSchoolModules.findIndex(
        (module) => module.name === "Kiosk"
      )

      return index !== -1
        ? JSON.parse(
            store.state.schools.activeSchoolModules[index].pivot.option_json
          )
        : false
    })

    const active = computed(() => {
      const index = store.state.schools.activeSchoolModules.findIndex(
        (module) => module.name === "Kiosk"
      )

      return index !== -1
        ? Boolean(store.state.schools.activeSchoolModules[index].pivot.status)
        : false
    })

    const setFormData = () => {
      state.form.urlKloginMethodOnly = Boolean(options.value.kurl)
      state.form.cardAsDefaultLogin = Boolean(options.value.usbcard)
      state.form.barcodeAsDefaultLogin = Boolean(options.value.barcode)
      state.form.hideStudentKioskPasscodeField = Boolean(
        options.value.spasscode
      )
      state.form.studentNumberAsKioskPasscode = Boolean(
        options.value.prefill_kiosk_passcode
      )
      state.form.defaultDomain = options.value.default_domain
      state.form.defaultDomain = options.value.default_domain
        ? options.value.default_domain
        : schoolDomains.value[0]
          ? schoolDomains.value[0]
          : ""
      state.form.studentNumOnly = Boolean(options.value.studentnumonly)
      if (state.form.studentNumOnly) {
        state.form.studentInputMethod = "studentNumberOnly"
      } else if (state.form.cardAsDefaultLogin) {
        state.form.studentInputMethod = "magneticStrip"
      } else if (state.form.barcodeAsDefaultLogin) {
        state.form.studentInputMethod = "barcode"
      } else {
        state.form.studentInputMethod = "emailPasscode"
      }
    }

    onMounted(() => {
      getSchoolDomains()
      if (options.value) {
        setFormData()
      } else {
        state.isHidden = true
      }
      state.isActive = active.value
    })

    const getSchoolDomains = () => {
      store
        .dispatch("schools/getSchoolDomains", currentUser.value.school_id)
        .then(() => {
          setFormData()
        })
    }

    const handleStudentInputMethodChange = (method) => {
      if (state.form.studentInputMethod === method) {
        const currentMethod = state.form.studentInputMethod
        state.form.studentInputMethod = null
        nextTick(() => {
          state.form.studentInputMethod = currentMethod
        })
        return
      }

      state.form.studentInputMethod = method
      switch (method) {
        case "emailPasscode":
          state.form.cardAsDefaultLogin = false
          state.form.barcodeAsDefaultLogin = false
          state.form.studentNumOnly = false
          break
        case "barcode":
          state.form.cardAsDefaultLogin = false
          state.form.barcodeAsDefaultLogin = true
          state.form.studentNumOnly = false
          state.form.studentNumberAsKioskPasscode = true
          break
        case "magneticStrip":
          state.form.cardAsDefaultLogin = true
          state.form.barcodeAsDefaultLogin = false
          state.form.studentNumOnly = false
          state.form.studentNumberAsKioskPasscode = true
          break
        case "studentNumberOnly":
          state.form.cardAsDefaultLogin = false
          state.form.barcodeAsDefaultLogin = false
          state.form.studentNumOnly = true
          state.form.studentNumberAsKioskPasscode = true
          break
      }
      submit()
    }

    const submit = () => {
      setTimeout(() => {
        resetResponseMessages()
        const inputsData = JSON.stringify({
          kurl: state.form.urlKloginMethodOnly,
          usbcard: state.form.cardAsDefaultLogin,
          barcode: state.form.barcodeAsDefaultLogin,
          spasscode: state.form.hideStudentKioskPasscodeField,
          studentnumonly: state.form.studentNumOnly,
          prefill_kiosk_passcode: state.form.studentNumberAsKioskPasscode,
          default_domain: state.form.defaultDomain
        })
        isLoading.value = true
        store
          .dispatch("modules/updateKioskModule", { option_json: inputsData })
          .then(() => {
            setSuccessResponse("Module updated.")
            getActiveModules()
            isLoading.value = false
          })
          .catch((err) => {
            const response = err.response.data
            setErrorResponse(response.message, response.errors)
            isLoading.value = false
            getActiveModules()
          })
      }, 100)
    }

    const getActiveModules = () => {
      store
        .dispatch("schools/getActiveModules", {
          clearCacheEntry: true
        })
        .then(() => {
          setFormData()
        })
    }

    const openKioskPasscodeActionDialog = (value) => {
      if (value) {
        actionDialog.open(submit, {
          args: {},
          props: {
            question:
              "This will overwrite any other passcode that has already been created for all Kiosk users. Are you sure you want to proceed?",
            confirmText: "Yes, overwrite all"
          }
        })
      } else {
        submit()
      }
    }

    watch(
      () => actionDialog.isVisible,
      (value) => {
        if (!value && !actionDialog.isConfirmed) {
          state.form.studentNumberAsKioskPasscode = false
          setPasscodeCheckbox.value++
        }
      }
    )

    const toggleKiosk = (status) => {
      store
        .dispatch("modules/toggleModule", {
          id: 3,
          moduleStatus: { status: status ? 0 : 1 }
        })
        .then(() => {
          state.isActive = !state.isActive
          store.dispatch("schools/getActiveModules", {
            clearCacheEntry: true
          })
        })
    }

    const resetResponseMessages = () => {
      state.reqResponse = {
        type: "",
        message: "",
        errors: {}
      }
    }

    const setSuccessResponse = (message) => {
      state.reqResponse = {
        type: "success",
        message: message ? message : "Success!",
        errors: {}
      }
    }

    const setErrorResponse = (message, errors) => {
      state.reqResponse = {
        type: "danger",
        message: message ? message : "Something went wrong!",
        errors: errors ? errors : {}
      }
    }

    return {
      state,
      schoolDomains,
      handleStudentInputMethodChange,
      submit,
      openKioskPasscodeActionDialog,
      toggleKiosk,
      setPasscodeCheckbox,
      isPasscodeCheckboxDisabled,
      resetResponseMessages
    }
  }
}
</script>
